import { useEffect, useMemo, useState } from "react";
import { Polygon, Polyline, Tooltip } from "react-leaflet";
import { api, apiError } from "@/lib/api";
import LeafletMap, { FitBounds, GeomanEditor, DraggableControls, polygonInside, polygonsOverlap, polygonAreaM2, colorForShop } from "@/components/map/LeafletMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, X, Loader2, PencilRuler, Store, TriangleAlert, Info, Pencil, Magnet, Scissors, Move, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { US_STATES } from "@/lib/usStates";
import { ZONE_CLASSES, CLASS_FEE } from "@/lib/zoneClasses";
import { classifyBoundary, STATE_NAMES } from "@/lib/geoClassify";
import BusinessLayers from "@/components/map/BusinessLayers";
import HeatLayer from "@/components/map/HeatLayer";
import { runProspectSync, syncReportText } from "@/lib/prospectSync";
import { useBusinessDiscovery } from "@/lib/useBusinessDiscovery";
import { BusinessLayerToggles } from "@/components/map/BusinessLayerToggles";
import ZoneDividerSplit from "@/components/admin/ZoneDividerSplit";
import ZoneTwoLineCreate from "@/components/admin/ZoneTwoLineCreate";

const NEW_ZONE = { id: null, name: "", polygon: [], state: "none", classification: "Standard", annual_fee: 99, shop_id: "none" };

export default function ZonesPanel({ embedBikePathId = null, initialPathId = null }) {
  const [paths, setPaths] = useState([]);
  const [shops, setShops] = useState([]);
  const [selectedPath, setSelectedPath] = useState("");
  const [fState, setFState] = useState("all");
  const [fCounty, setFCounty] = useState("all");
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...NEW_ZONE });
  const [saving, setSaving] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [snap, setSnap] = useState(true);
  const [moveMode, setMoveMode] = useState(false);
  const [dividerZone, setDividerZone] = useState(null);
  const [twoLineOpen, setTwoLineOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [deleted, setDeleted] = useState([]);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileData, setReconcileData] = useState(null);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileHealth, setReconcileHealth] = useState({});
  const [reconcileScope, setReconcileScope] = useState(null);
  const [syncingProspects, setSyncingProspects] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [overlapPairs, setOverlapPairs] = useState([]);
  const [nearbyZones, setNearbyZones] = useState([]);
  const [showNearby, setShowNearby] = useState(true);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [returnTo, setReturnTo] = useState(null);
  const [syncBuffer, setSyncBuffer] = useState("0.5");

  const loadBase = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/bike-paths"), api.get("/portal/shops-basic")]);
      setPaths(p.data); setShops(s.data);
      if (p.data.length && !selectedPath) setSelectedPath(embedBikePathId || (p.data.some((x) => x.id === initialPathId) ? initialPathId : p.data[0].id));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const orphanZones = Object.values(reconcileHealth).filter((b) => b.fromDeletedZone > 0);
  const orphanTotal = orphanZones.reduce((s, b) => s + b.fromDeletedZone, 0);
  const orphanZoneCount = orphanZones.length;

  const fetchHealth = async (pid) => {
    if (!pid) { setReconcileHealth({}); return; }
    try { const { data } = await api.post("/admin/zones/reconcile-prospects", { dry_run: true, bike_path_id: pid }); setReconcileHealth(data.byZone || {}); }
    catch { setReconcileHealth({}); }
  };

  const loadZones = async (pid) => {
    api.get("/admin/zones/overlaps").then(({ data }) => setOverlapPairs(data.pairs || [])).catch(() => {});
    if (!pid) { setZones([]); setNearbyZones([]); setReconcileHealth({}); return; }
    api.get("/admin/zones/nearby", { params: { bike_path_id: pid } }).then(({ data }) => setNearbyZones(data || [])).catch(() => setNearbyZones([]));
    try { const { data } = await api.get("/zones", { params: { bike_path_id: pid } }); setZones(data); fetchHealth(pid); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadBase(); }, []);
  useEffect(() => { if (embedBikePathId) setSelectedPath(embedBikePathId); }, [embedBikePathId]);
  useEffect(() => {
    if (form.state !== "none" || (form.polygon?.length || 0) < 3) return;
    const st = classifyBoundary(form.polygon).state;
    if (st && US_STATES.includes(st)) setForm((f) => (f.state === "none" ? { ...f, state: st } : f));
  }, [form.polygon, form.state]);
  useEffect(() => { loadZones(selectedPath); loadDeleted(); setCreating(false); setForm({ ...NEW_ZONE }); setDividerZone(null); }, [selectedPath]);
  useEffect(() => {
    if (!selectedPath) { setCoverage(null); return; }
    api.get(`/bike-paths/${selectedPath}/coverage`).then((r) => setCoverage(r.data)).catch(() => setCoverage(null));
  }, [selectedPath, zones]);

  const path = paths.find((p) => p.id === selectedPath);

  const withGeo = useMemo(() => paths.map((p) => ({ ...p, geo: classifyBoundary(p.boundary || []) })), [paths]);
  const stateOptions = useMemo(() => Array.from(new Set(withGeo.map((p) => p.geo.state).filter(Boolean))).sort(), [withGeo]);
  const countyOptions = useMemo(() => (fState === "all" ? [] : Array.from(new Set(withGeo.filter((p) => p.geo.state === fState).map((p) => p.geo.county).filter(Boolean))).sort()), [withGeo, fState]);
  const filteredPaths = useMemo(() => withGeo.filter((p) => {
    if (fState !== "all" && p.geo.state !== fState) return false;
    if (fCounty !== "all" && p.geo.county !== fCounty) return false;
    return true;
  }), [withGeo, fState, fCounty]);
  useEffect(() => {
    if (!filteredPaths.length) { if (selectedPath) setSelectedPath(""); return; }
    if (!filteredPaths.some((p) => p.id === selectedPath)) setSelectedPath(filteredPaths[0].id);
  }, [filteredPaths]); // eslint-disable-line react-hooks/exhaustive-deps

  const biz = useBusinessDiscovery(path?.boundary, { bikePathId: path?.id });

  const otherZones = [...zones, ...nearbyZones].filter((z) => z.id !== form.id);
  const readOnlyZones = zones.filter((z) => z.id !== form.id);
  const legend = Array.from(new Map(zones.filter((z) => z.shop_id).map((z) => [z.shop_id, z.shop_name || "Shop"])).entries());

  // Live territory warnings for the shape currently being drawn/edited
  const warnings = (() => {
    const poly = form.polygon;
    if (!creating || !poly || poly.length < 3) return {};
    const out = {};
    if (path?.boundary?.length >= 3 && !polygonInside(poly, path.boundary)) out.outside = true;
    const hit = otherZones.find((z) => polygonsOverlap(poly, z.polygon || []));
    if (hit) out.overlap = hit.name;
    const area = polygonAreaM2(poly);
    const others = zones.filter((z) => z.id !== form.id).map((z) => polygonAreaM2(z.polygon || [])).filter((a) => a > 0);
    if (others.length) {
      const avg = others.reduce((s, a) => s + a, 0) / others.length;
      if (area > avg * 2.2) out.balance = "much larger";
      else if (area < avg * 0.45) out.balance = "much smaller";
    }
    return out;
  })();

  const saveZone = async () => {
    if (!form.polygon || form.polygon.length < 3) return toast.error("Draw a zone boundary with at least 3 points");
    if (otherZones.some((z) => polygonsOverlap(form.polygon, z.polygon || []))) return toast.error("Zones cannot overlap — adjust the boundary");
    setSaving(true);
    try {
      const body = { bike_path_id: selectedPath, name: form.name.trim() || null, polygon: form.polygon, state: form.state === "none" ? null : form.state, classification: form.classification, annual_fee: form.annual_fee };
      if (form.id) {
        await api.put(`/zones/${form.id}`, body);
      } else {
        body.shop_id = form.shop_id === "none" ? null : form.shop_id;
        await api.post("/zones", body);
      }
      toast.success(form.id ? "Zone updated" : "Zone created");
      setCreating(false); setForm({ ...NEW_ZONE });
      loadZones(selectedPath); loadBase();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const startEditShape = (z) => {
    if (z.status === "LEASED") toast.warning("This zone is leased — you can edit its name, fee, state and class, but you must return the lease before reshaping its boundary.");
    setMoveMode(false); setForm({ id: z.id, name: z.name, polygon: z.polygon || [], state: STATE_NAMES[z.state] || z.state || classifyBoundary(z.polygon || []).state || "none", classification: z.classification || "Standard", annual_fee: z.annual_fee ?? CLASS_FEE[z.classification] ?? 99, shop_id: z.shop_id || "none" }); setCreating(true); };

  const jumpToZone = (z) => {
    if (!z.bike_path_id) return;
    if (selectedPath) setReturnTo((r) => r || { id: selectedPath, name: path?.name, fState, fCounty });
    setFState("all"); setFCounty("all"); setPendingEdit(z.id); setSelectedPath(z.bike_path_id);
    toast.info(`Switched to ${z.bikePath || "its bike path"} — editing ${z.name}`);
  };

  const goBack = () => {
    if (!returnTo) return;
    setFState(returnTo.fState); setFCounty(returnTo.fCounty); setSelectedPath(returnTo.id); setReturnTo(null);
  };

  useEffect(() => {
    if (!pendingEdit) return;
    const z = zones.find((x) => x.id === pendingEdit);
    if (!z) return;
    setPendingEdit(null); startEditShape(z); window.scrollTo({ top: 0, behavior: "smooth" });
  }, [zones, pendingEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const reassign = async (zoneId, shopId) => {
    try { await api.patch(`/zones/${zoneId}/assign`, { shop_id: shopId === "none" ? null : shopId }); toast.success("Zone assignment updated"); loadZones(selectedPath); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const loadDeleted = async () => {
    if (!selectedPath) { setDeleted([]); return; }
    try { const { data } = await api.get(`/zones/deleted?bike_path_id=${selectedPath}`); setDeleted(data); }
    catch { setDeleted([]); }
  };

  const restoreZone = async (zoneId) => {
    try { await api.post(`/zones/${zoneId}/restore`); toast.success("Zone restored"); loadZones(selectedPath); loadBase(); loadDeleted(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const delZone = async (zoneId) => {
    try {
      const { data } = await api.delete(`/zones/${zoneId}`);
      const r = data?.rehomed;
      const moved = r ? (r.prospects + r.advertisers) : 0;
      toast.success(moved > 0 ? `Zone deleted — re-homed ${r.prospects} prospect(s) & ${r.advertisers} advertiser(s)` : "Zone deleted",
        { action: { label: "Undo", onClick: () => restoreZone(zoneId) }, duration: 8000 });
      setDel(null); loadZones(selectedPath); loadBase(); loadDeleted();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const targetSummary = (targets) => {
    const named = (targets || []).filter((t) => t.zoneId).map((t) => `Zone ${t.zoneNumber ?? "?"}`);
    if (named.length === 0) return "unassigned";
    if (named.length === 1) return named[0];
    return `their nearest zones (${named.slice(0, 3).join(", ")}${named.length > 3 ? "…" : ""})`;
  };

  const undoSplit = async (z) => {
    if (!window.confirm(`Undo the split for "${z.name}"? The pieces merge back into the original zone (gap filled), and all businesses, prospects, routes and content move back to it.`)) return;
    try {
      const { data } = await api.post(`/zones/${z.id}/undo-split`);
      toast.success(`Split undone — ${data.merged} piece${data.merged === 1 ? "" : "s"} merged back`);
      loadZones(selectedPath);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openDelete = async (z) => {
    setDel({ zone: z, impact: null, loading: true });
    try { const { data } = await api.get(`/zones/${z.id}/delete-impact`); setDel({ zone: z, impact: data, loading: false }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setDel(null); }
  };

  const openReconcile = async (zoneId = null) => {
    if (!selectedPath) return;
    setReconcileScope({ zoneId, label: zoneId ? (zones.find((z) => z.id === zoneId)?.name || "zone") : (path?.name || "this path") });
    setReconcileOpen(true); setReconcileData(null); setReconcileBusy(true);
    try {
      const body = zoneId ? { dry_run: true, zone_id: zoneId } : { dry_run: true, bike_path_id: selectedPath };
      const { data } = await api.post("/admin/zones/reconcile-prospects", body);
      setReconcileData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setReconcileOpen(false); }
    finally { setReconcileBusy(false); }
  };

  const applyReconcile = async () => {
    setReconcileBusy(true);
    try {
      const body = reconcileScope?.zoneId ? { dry_run: false, zone_id: reconcileScope.zoneId } : { dry_run: false, bike_path_id: selectedPath };
      const { data } = await api.post("/admin/zones/reconcile-prospects", body);
      toast.success(`Re-homed ${data.applied} prospect${data.applied === 1 ? "" : "s"}`);
      setReconcileOpen(false); setReconcileData(null);
      loadZones(selectedPath);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setReconcileBusy(false); }
  };

  const syncProspects = async (all = false) => {
    if (!all && !selectedPath) return;
    if (all && !window.confirm("Sync prospects for EVERY zone on every bike path? This can take a while and uses Google Places requests for each zone.")) return;
    setSyncingProspects(true); setSyncJob(null);
    try {
      const body = { buffer_miles: parseFloat(syncBuffer) || 0.5, ...(all ? {} : zones.length ? { zone_ids: zones.map((z) => z.id) } : { bike_path_id: selectedPath }) };
      const job = await runProspectSync(body, setSyncJob);
      const r = job.report || {};
      if (r.zones_failed) toast.warning(`Synced with issues: ${r.zones_failed} zone(s) couldn't reach Google — try again shortly.`);
      else toast.success(`Prospects synced: ${syncReportText(job)}`);
      loadZones(selectedPath); biz.refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setSyncingProspects(false); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (!paths.length) return <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="zones-no-paths">Create a bike path first — zones live inside a bike path.</div>;

  const shopColor = (z) => colorForShop(z.shop_id);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          {!embedBikePathId && <>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">State</Label>
            <Select value={fState} onValueChange={(v) => { setFState(v); setFCounty("all"); }}>
              <SelectTrigger className="w-44" data-testid="zones-filter-state"><SelectValue placeholder="All states" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All states</SelectItem>
                {stateOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">County</Label>
            <Select value={fCounty} onValueChange={setFCounty} disabled={fState === "all"}>
              <SelectTrigger className="w-44" data-testid="zones-filter-county"><SelectValue placeholder="All counties" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All counties</SelectItem>
                {countyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Bike path</Label>
            <Select value={selectedPath} onValueChange={setSelectedPath}>
              <SelectTrigger className="w-64" data-testid="zones-path-select"><SelectValue placeholder="Select a bike path" /></SelectTrigger>
              <SelectContent className="max-h-72">{filteredPaths.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          </>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Select value={syncBuffer} onValueChange={setSyncBuffer} disabled={!!selectedPath && !zones.length}>
              <SelectTrigger className="w-[104px]" data-testid="sync-buffer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0.5">0.5 mi</SelectItem>
                <SelectItem value="1">1 mi</SelectItem>
                <SelectItem value="2">2 mi</SelectItem>
                <SelectItem value="3">3 mi</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => syncProspects(false)} disabled={!selectedPath || syncingProspects} title={selectedPath && !zones.length ? "No zones yet — searches the whole bike path corridor" : undefined} data-testid="sync-prospects-button">{syncingProspects ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}{syncingProspects && syncJob ? `Syncing ${syncJob.done}/${syncJob.total}…` : "Sync prospects"}</Button>
            <Button variant="outline" onClick={() => syncProspects(true)} disabled={syncingProspects} data-testid="sync-all-paths-button" title="Sync every zone on every bike path"><RefreshCw className="w-4 h-4 mr-1" />Sync all paths</Button>
          </div>
          {syncJob && (
            <div className="w-full sm:w-auto text-xs rounded-lg border border-border bg-card px-3 py-2" data-testid="sync-report">
              {syncJob.status === "running" ? <span>Syncing zone {Math.min(syncJob.done + 1, syncJob.total)} of {syncJob.total}{syncJob.current ? ` — ${syncJob.current}` : ""}…</span> : <span><b>Last sync:</b> {syncReportText(syncJob)}{syncJob.report?.zones_capped ? ` · ${syncJob.report.zones_capped} zone(s) hit the search limit` : ""}</span>}
              {syncJob.status !== "running" && <button type="button" className="ml-2 text-muted-foreground underline" onClick={() => setSyncJob(null)} data-testid="sync-report-dismiss">Dismiss</button>}
            </div>
          )}
          {returnTo && returnTo.id !== selectedPath && (
            <Button variant="outline" className="border-purple-500/60 text-purple-500" onClick={goBack} data-testid="back-to-previous-path-button"><Undo2 className="w-4 h-4 mr-1" />Back to {returnTo.name || "previous path"}</Button>
          )}
          <Button variant="outline" onClick={() => openReconcile()} disabled={!selectedPath} data-testid="reconcile-prospects-button"><RefreshCw className="w-4 h-4 mr-1" />Reconcile prospects</Button>
          {!creating ? (
            <>
              <Button variant="outline" onClick={() => setTwoLineOpen(true)} disabled={!selectedPath || (path?.boundary?.length || 0) < 3} data-testid="new-zone-two-line-button"><Scissors className="w-4 h-4 mr-1" />New zone (2 lines)</Button>
              <Button onClick={() => { setMoveMode(false); setCreating(true); setForm({ ...NEW_ZONE }); }} data-testid="new-zone-button"><Plus className="w-4 h-4 mr-1" />New zone</Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => { setCreating(false); setMoveMode(false); setForm({ ...NEW_ZONE }); }} data-testid="cancel-zone-button"><X className="w-4 h-4 mr-1" />Cancel</Button>
          )}
        </div>
      </div>

      {coverage && (
        <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="zone-coverage-banner">
          <span className="font-medium">{coverage.zone_count} zone{coverage.zone_count === 1 ? "" : "s"}</span>
          <span className="text-muted-foreground">{coverage.assigned_zone_count} assigned</span>
          <span className="text-muted-foreground">~{coverage.coverage_pct}% of path covered</span>
          {coverage.coverage_pct < 60 && (
            <span className="flex items-center gap-1.5 text-amber-600" data-testid="coverage-gap-warning"><TriangleAlert className="w-4 h-4" />Large area uncovered — consider adding zones to fill gaps.</span>
          )}
          {coverage.coverage_pct >= 95 && (
            <span className="flex items-center gap-1.5 text-primary" data-testid="coverage-full"><Info className="w-4 h-4" />This path is well covered.</span>
          )}
        </div>
      )}

      {overlapPairs.length > 0 && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500 space-y-1" data-testid="zone-overlaps-banner">
          <div className="flex items-center gap-2 font-medium"><TriangleAlert className="w-4 h-4 shrink-0" />{overlapPairs.length} pair{overlapPairs.length === 1 ? "" : "s"} of existing zones overlap. Reshape or delete one zone in each pair.</div>
          {overlapPairs.slice(0, 8).map((p) => (
            <div key={`${p.a.id}-${p.b.id}`} className="text-xs pl-6" data-testid={`zone-overlap-${p.a.id}-${p.b.id}`}>{p.a.name} ({p.a.bikePath || "no path"}) ↔ {p.b.name} ({p.b.bikePath || "no path"}){p.crossPath ? " · different bike paths" : ""}</div>
          ))}
        </div>
      )}

      {orphanTotal > 0 && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="orphan-prospects-banner">
          <span className="flex items-center gap-2 text-red-500"><TriangleAlert className="w-4 h-4 shrink-0" /><span><b data-testid="orphan-prospects-count">{orphanTotal}</b> orphaned prospect{orphanTotal === 1 ? "" : "s"} in {orphanZoneCount} zone{orphanZoneCount === 1 ? "" : "s"} on this path {orphanTotal === 1 ? "is" : "are"} still linked to deleted zones.</span></span>
          <Button size="sm" variant="outline" className="border-red-500/50 text-red-500" onClick={() => openReconcile()} data-testid="orphan-reconcile-button"><RefreshCw className="w-4 h-4 mr-1" />Review & reconcile</Button>
        </div>
      )}

      {creating && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="zone-create-form">
          <div className="text-sm text-muted-foreground flex items-center gap-2"><PencilRuler className="w-4 h-4 text-primary shrink-0" />Use the polygon tool (top-right of the map) to draw the zone boundary point-by-point. Drag vertices to refine, or flip on "Move whole zone" to drag the entire shape. Zones cannot overlap, but may extend beyond the bike path boundary.</div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 w-fit">
              <Magnet className={`w-4 h-4 ${snap ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-sm">Snap to boundary & neighbours</span>
              <Switch checked={snap} onCheckedChange={setSnap} data-testid="zone-snap-toggle" />
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 w-fit">
              <Move className={`w-4 h-4 ${moveMode ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-sm">Move whole zone</span>
              <Switch checked={moveMode} onCheckedChange={setMoveMode} data-testid="zone-move-toggle" disabled={(form.polygon?.length || 0) < 3} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div><Label>Zone name <span className="text-muted-foreground font-normal">(optional)</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Auto-named if left blank" data-testid="zone-name-input" /></div>
            <div>
              <Label>State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="zone-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">No state</SelectItem>
                  {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class</Label>
              <Select value={form.classification} onValueChange={(v) => setForm({ ...form, classification: v, annual_fee: CLASS_FEE[v] })}>
                <SelectTrigger data-testid="zone-class-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ZONE_CLASSES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name} — ${c.fee}/yr</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign to</Label>
              <Select value={form.shop_id} onValueChange={(v) => setForm({ ...form, shop_id: v })}>
                <SelectTrigger data-testid="zone-shop-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned (available)</SelectItem>
                  <SelectItem value="velopaths">Velopaths (managed)</SelectItem>
                  {shops.filter((s) => s.approved && s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="zone-vertex-readout">{form.polygon?.length >= 3 ? `Boundary: ${form.polygon.length} points · ~${(polygonAreaM2(form.polygon) / 1e6).toFixed(2)} km²` : "No boundary drawn yet — use the polygon tool on the map."}</div>
          {warnings.outside && <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="zone-warn-outside"><TriangleAlert className="w-3.5 h-3.5" />Heads up: part of this zone extends beyond the bike path boundary. That's allowed — save if intended.</div>}
          {warnings.overlap && <div className="text-xs text-destructive flex items-center gap-1.5" data-testid="zone-warn-overlap"><TriangleAlert className="w-3.5 h-3.5" />Overlaps "{warnings.overlap}" — zones must not overlap.</div>}
          {warnings.balance && <div className="text-xs text-amber-600 flex items-center gap-1.5" data-testid="zone-warn-balance"><TriangleAlert className="w-3.5 h-3.5" />This territory is {warnings.balance} than the others — consider balancing zone sizes.</div>}
          <div className="flex justify-end"><Button onClick={saveZone} disabled={saving} data-testid="save-zone-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save zone</Button></div>
        </div>
      )}

      {(zones.length > 0 || nearbyZones.length > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="zone-color-legend">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#FACC15", border: "1px solid #EF4444" }} />Service zones</span>
          {nearbyZones.length > 0 && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="nearby-zones-toggle">
              <input type="checkbox" checked={showNearby} onChange={(e) => setShowNearby(e.target.checked)} data-testid="nearby-zones-checkbox" />
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: "rgba(168,85,247,0.25)", border: "1px dashed #A855F7" }} />
              <span data-testid="nearby-zones-count">Zones on other bike paths ({nearbyZones.length})</span>
            </label>
          )}
        </div>
      )}

      <div className="relative rounded-2xl overflow-hidden border border-border" data-testid="zones-map-wrap">
      <LeafletMap height={920} center={path?.boundary?.length ? path.boundary[0] : [41.75, -71.3]}>
        <DraggableControls />
        {path?.boundary?.length >= 3 && <Polygon positions={path.boundary} pathOptions={{ color: "#64748B", weight: 2, fillOpacity: 0.05, dashArray: "6" }} />}
        {(path?.lines?.length ? path.lines : (path?.centerline?.length >= 2 ? [path.centerline] : [])).map((ln, i) => (ln?.length >= 2 ? <Polyline key={`route-${i}`} positions={ln} pathOptions={{ color: "#0ea5e9", weight: 5, opacity: 0.95, interactive: false }} /> : null))}
        {path?.boundary?.length >= 2 && <FitBounds bounds={path.boundary} />}
        {showNearby && nearbyZones.map((z) => (
          (z.polygon?.length >= 3) && <Polygon key={`nb-${z.id}`} positions={z.polygon} pathOptions={{ color: "#A855F7", fillColor: "#A855F7", fillOpacity: 0.15, weight: 2, dashArray: "5 4" }} eventHandlers={{ click: () => { if (!creating) jumpToZone(z); } }}>
            <Tooltip>{`${z.name} · ${z.bikePath || "another bike path"} (other path)${z.bike_path_id && !creating ? " — click to edit" : ""}`}</Tooltip>
          </Polygon>
        ))}
        {readOnlyZones.map((z) => (
          (z.polygon?.length >= 3) && <Polygon key={z.id} positions={z.polygon} pathOptions={{ color: "#EF4444", fillColor: "#FACC15", fillOpacity: 0.2, weight: 2 }}>
            <Tooltip>{z.shop_name ? `${z.name} · ${z.shop_name}` : `${z.name} · Unassigned`}</Tooltip>
          </Polygon>
        ))}
        {creating && <GeomanEditor polygon={form.polygon} onChange={(poly) => setForm((f) => ({ ...f, polygon: poly }))} color="#22D3EE" snap={snap} dragMode={moveMode} snapTo={[path?.boundary, ...otherZones.map((z) => z.polygon)].filter((r) => r && r.length >= 3)} />}
        {biz.visibleBiz.length > 0 && <BusinessLayers businesses={biz.visibleBiz} onShopAction={biz.handleShopAction} />}
        {biz.heatOn && <HeatLayer points={biz.heatPoints} enabled={biz.heatOn} />}
      </LeafletMap>
      {path?.boundary?.length >= 3 && <BusinessLayerToggles {...biz} />}
      </div>

      <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="zones-list">
        {zones.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground" data-testid="zones-empty">No zones in this bike path yet.</div>
        ) : zones.map((z) => (
          <div key={z.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`zone-row-${z.id}`}>
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: "#FACC15", border: "1px solid #EF4444" }} />
            <div className="flex-1 min-w-[160px]">
              <div className="font-medium flex items-center gap-2"><span className="inline-flex items-center rounded-md bg-primary/10 text-primary font-heading font-bold tabular-nums text-xs px-1.5 py-0.5" data-testid={`zone-number-${z.id}`}>#{z.zone_number ?? "—"}</span>{z.name}</div>
              <div className="text-xs text-muted-foreground">{z.polygon?.length || 0}-point boundary · ~{(polygonAreaM2(z.polygon || []) / 1e6).toFixed(2)} km²</div>
              <div className="text-xs flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5" data-testid={`zone-stats-${z.id}`}>
                <span className="text-muted-foreground">{z.businessCount ?? 0} business{(z.businessCount ?? 0) === 1 ? "" : "es"}</span>
                <span className="text-muted-foreground">Opportunity <b className="text-foreground tabular-nums">{z.opportunityScore ?? 0}</b></span>
                <span className="text-emerald-600 tabular-nums">~${Math.round(z.projectedMonthly ?? 0)}/mo</span>
              </div>
              {reconcileHealth[z.id]?.fromDeletedZone > 0 && (
                <div className="text-xs text-red-500 font-medium flex items-center gap-1 mt-0.5" data-testid={`zone-orphan-alert-${z.id}`}><TriangleAlert className="w-3.5 h-3.5 shrink-0" />{reconcileHealth[z.id].fromDeletedZone} orphaned prospect{reconcileHealth[z.id].fromDeletedZone === 1 ? " is" : "s are"} still linked to a deleted zone — reconcile to move {reconcileHealth[z.id].fromDeletedZone === 1 ? "it" : "them"} here</div>
              )}
              {reconcileHealth[z.id]?.fromLiveZone > 0 && (
                <div className="text-xs text-amber-600 flex items-center gap-1 mt-0.5" data-testid={`zone-mismatch-${z.id}`}><TriangleAlert className="w-3.5 h-3.5 shrink-0" />{reconcileHealth[z.id].fromLiveZone} prospect{reconcileHealth[z.id].fromLiveZone === 1 ? "" : "s"} inside this zone {reconcileHealth[z.id].fromLiveZone === 1 ? "is" : "are"} assigned to another zone</div>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => startEditShape(z)} data-testid={`edit-zone-${z.id}`}><Pencil className="w-4 h-4 mr-1" />Edit shape</Button>
            <Button size="sm" variant="outline" onClick={() => setDividerZone(z)} disabled={(z.polygon?.length || 0) < 3 || z.status === "LEASED"} title={z.status === "LEASED" ? "Leased zones can't be split — return the lease first" : undefined} data-testid={`split-zone-${z.id}`}><Scissors className="w-4 h-4 mr-1" />Split (divider line)</Button>
            {(z.split_from_zone_id || zones.some((c) => c.split_from_zone_id === z.id)) && (
              <Button size="sm" variant="outline" onClick={() => undoSplit(z)} data-testid={`undo-split-${z.id}`}><Undo2 className="w-4 h-4 mr-1" />Undo split</Button>
            )}
            {z.created_via === "two_line" && (
              <Button size="sm" variant="outline" onClick={() => openDelete(z)} data-testid={`undo-two-line-${z.id}`}><Undo2 className="w-4 h-4 mr-1" />Undo 2-line zone</Button>
            )}
            {reconcileHealth[z.id]?.reassign > 0 && (
              <Button size="sm" variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" onClick={() => openReconcile(z.id)} data-testid={`reconcile-zone-${z.id}`}><RefreshCw className="w-4 h-4 mr-1" />Reconcile</Button>
            )}
            <div className="w-52 flex items-center gap-2">
              <Store className="w-4 h-4 text-muted-foreground shrink-0" />
              <Select value={z.shop_id ? z.shop_id : (z.status === "VELOPATHS_MANAGED" ? "velopaths" : "none")} onValueChange={(v) => reassign(z.id, v)}>
                <SelectTrigger data-testid={`zone-assign-${z.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  <SelectItem value="velopaths">Velopaths</SelectItem>
                  {shops.filter((s) => s.approved && s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => openDelete(z)} data-testid={`delete-zone-${z.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      {deleted.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid="recently-deleted">
          <div className="text-sm font-medium flex items-center gap-2"><RefreshCw className="w-4 h-4" />Recently deleted — restorable for 24h</div>
          {deleted.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`deleted-zone-${d.id}`}>
              <span className="text-muted-foreground truncate"><span className="font-heading font-bold text-foreground tabular-nums">#{d.zoneNumber ?? "—"}</span> {d.name}</span>
              <Button size="sm" variant="outline" onClick={() => restoreZone(d.id)} data-testid={`restore-zone-${d.id}`}><RefreshCw className="w-4 h-4 mr-1" />Restore</Button>
            </div>
          ))}
        </div>
      )}

      {twoLineOpen && (
        <ZoneTwoLineCreate
          bikePath={{ id: selectedPath, name: path?.name, boundary: path?.boundary || [] }}
          zones={zones}
          nearbyZones={nearbyZones}
          open={twoLineOpen}
          onOpenChange={setTwoLineOpen}
          onDone={() => { setTwoLineOpen(false); loadZones(selectedPath); loadBase(); }}
        />
      )}

      <AlertDialog open={!!del} onOpenChange={(o) => { if (!o) setDel(null); }}>
        <AlertDialogContent data-testid="delete-zone-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete zone "{del?.zone?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {del?.loading && <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Checking impact…</div>}
                {del?.impact?.leased && (
                  <div className="text-destructive text-sm flex items-center gap-1.5" data-testid="delete-leased-warn"><TriangleAlert className="w-4 h-4 shrink-0" />This zone is leased. Return or cancel the lease before deleting it.</div>
                )}
                {del?.impact && !del.impact.leased && (
                  <div className="text-sm" data-testid="delete-impact">
                    {(del.impact.prospects + del.impact.advertisers) > 0 ? (
                      <>This will move <b>{del.impact.prospects}</b> prospect{del.impact.prospects === 1 ? "" : "s"} and <b>{del.impact.advertisers}</b> advertiser{del.impact.advertisers === 1 ? "" : "s"} {del.impact.hasNeighbor ? <>to {targetSummary(del.impact.targets)}</> : <>to <b>unassigned</b> (no neighbouring zone — recover later via Reconcile prospects)</>}.</>
                    ) : <>No prospects or advertisers are tied to this zone.</>}
                    {(del.impact.content + del.impact.routes) > 0 && <div className="text-xs text-muted-foreground mt-1">{del.impact.content} content item{del.impact.content === 1 ? "" : "s"} and {del.impact.routes} route{del.impact.routes === 1 ? "" : "s"} will also move.</div>}
                    <div className="text-xs text-muted-foreground mt-1">This cannot be undone.</div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={del?.loading || del?.impact?.leased} className={(del?.loading || del?.impact?.leased) ? "opacity-50 pointer-events-none" : ""} onClick={() => del?.zone && delZone(del.zone.id)} data-testid="delete-confirm">Delete zone</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {dividerZone && (
        <ZoneDividerSplit
          zone={{ zoneId: dividerZone.id, zoneNumber: dividerZone.zone_number ?? dividerZone.human_id ?? "", name: dividerZone.name }}
          open={!!dividerZone}
          businesses={biz.visibleBiz}
          onShopAction={biz.handleShopAction}
          onOpenChange={(o) => { if (!o) setDividerZone(null); }}
          onDone={() => { setDividerZone(null); loadZones(selectedPath); loadBase(); }}
        />
      )}

      <Dialog open={reconcileOpen} onOpenChange={(o) => { if (!reconcileBusy) { setReconcileOpen(o); if (!o) setReconcileData(null); } }}>
        <DialogContent className="max-w-lg" data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-primary" />Reconcile prospects — {reconcileScope?.label || path?.name || "this path"}</DialogTitle>
            <DialogDescription>Reassigns prospects to the zone whose boundary physically contains them, repairing orphans left by past zone splits or deletions. This preview makes no changes.</DialogDescription>
          </DialogHeader>
          {reconcileBusy && !reconcileData ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : reconcileData ? (
            <div className="space-y-3" data-testid="reconcile-report">
              <div className="text-sm text-muted-foreground">Scanned {reconcileData.prospectsScanned} prospects across {reconcileData.targetsScanned} zone{reconcileData.targetsScanned === 1 ? "" : "s"}.</div>
              {reconcileData.totalReassignable === 0 ? (
                <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm flex items-center gap-2" data-testid="reconcile-none"><Info className="w-4 h-4 text-primary" />Everything is already correctly assigned — no changes needed.</div>
              ) : (
                <>
                  <div className="text-sm font-medium">{reconcileData.totalReassignable} prospect{reconcileData.totalReassignable === 1 ? "" : "s"} would be re-homed:</div>
                  <div className="rounded-lg border border-border divide-y divide-border max-h-64 overflow-auto">
                    {Object.entries(reconcileData.byZone).map(([zid, b]) => (
                      <div key={zid} className="p-3 flex items-center justify-between gap-3 text-sm" data-testid={`reconcile-zone-${zid}`}>
                        <span className="font-medium">{b.zoneName || "Unnamed zone"}</span>
                        <span className="text-muted-foreground text-xs">+{b.reassign} ({b.fromDeletedZone} from deleted · {b.fromLiveZone} from other zones)</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { if (!reconcileBusy) { setReconcileOpen(false); setReconcileData(null); } }} data-testid="reconcile-cancel">Close</Button>
            <Button onClick={applyReconcile} disabled={reconcileBusy || !reconcileData || reconcileData.totalReassignable === 0} data-testid="reconcile-apply">{reconcileBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Apply fix</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
